import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Filter, Calendar, MapPin, DollarSign, Star, Music } from 'lucide-react'

interface FilterPanelProps {
  isOpen: boolean
  onClose: () => void
  filters: any
  onFiltersChange: (filters: any) => void
  type: 'events' | 'artists' | 'marketplace' | 'forums'
}

const FilterPanel: React.FC<FilterPanelProps> = ({ 
  isOpen, 
  onClose, 
  filters, 
  onFiltersChange, 
  type 
}) => {
  const eventGenres = ['Rock', 'Jazz', 'Electronic', 'Folk', 'Hip Hop', 'Country', 'Classical', 'Blues', 'Pop', 'Indie']
  const priceRanges = ['Free', '$1-20', '$21-50', '$51-100', '$100+']
  const distances = ['2 miles', '5 miles', '10 miles', '25 miles', 'Any distance']

  const marketplaceCategories = ['Guitars', 'Amplifiers', 'Drums', 'Keyboards', 'Microphones', 'Audio Gear', 'Accessories']
  const conditions = ['New', 'Excellent', 'Good', 'Fair']
  const priceBuckets = ['Under $100', '$100-500', '$500-1000', '$1000-2000', '$2000+']

  const forumCategories = ['General Discussion', 'Gear Talk', 'Live Music', 'Recording', 'Venues', 'Buying/Selling']

  const updateFilter = (key: string, value: any) => {
    onFiltersChange({ ...filters, [key]: value })
  }

  const toggleArrayFilter = (key: string, value: string) => {
    const current = filters[key] || []
    const updated = current.includes(value)
      ? current.filter((item: string) => item !== value)
      : [...current, value]
    updateFilter(key, updated)
  }

  const clearFilters = () => {
    onFiltersChange({})
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-40 lg:relative lg:inset-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Mobile Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm lg:hidden"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            className="absolute top-0 right-0 h-full w-80 lg:relative lg:w-full lg:h-auto"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          >
            <div className="glass-card h-full lg:h-auto p-6 overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <Filter className="text-le-accent" size={20} />
                  <h3 className="font-display font-semibold text-white">Filters</h3>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={clearFilters}
                    className="text-sm text-white/60 hover:text-white transition-colors duration-200"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={onClose}
                    className="p-1 text-white/60 hover:text-white transition-colors duration-200 lg:hidden"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                {/* Event Filters */}
                {type === 'events' && (
                  <>
                    {/* Date Range */}
                    <div>
                      <label className="block text-white font-medium mb-3">Date Range</label>
                      <div className="space-y-2">
                        <input
                          type="date"
                          value={filters.startDate || ''}
                          onChange={(e) => updateFilter('startDate', e.target.value)}
                          className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-le-accent"
                        />
                        <input
                          type="date"
                          value={filters.endDate || ''}
                          onChange={(e) => updateFilter('endDate', e.target.value)}
                          className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-le-accent"
                        />
                      </div>
                    </div>

                    {/* Genres */}
                    <div>
                      <label className="block text-white font-medium mb-3">Genres</label>
                      <div className="grid grid-cols-2 gap-2">
                        {eventGenres.map((genre) => (
                          <button
                            key={genre}
                            onClick={() => toggleArrayFilter('genres', genre)}
                            className={`px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                              (filters.genres || []).includes(genre)
                                ? 'bg-le-accent text-white'
                                : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                          >
                            {genre}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Price */}
                    <div>
                      <label className="block text-white font-medium mb-3">Price Range</label>
                      <div className="space-y-2">
                        {priceRanges.map((range) => (
                          <button
                            key={range}
                            onClick={() => updateFilter('priceRange', range)}
                            className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                              filters.priceRange === range
                                ? 'bg-le-accent text-white'
                                : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                          >
                            {range}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Marketplace Filters */}
                {type === 'marketplace' && (
                  <>
                    {/* Category */}
                    <div>
                      <label className="block text-white font-medium mb-3">Category</label>
                      <div className="space-y-2">
                        {marketplaceCategories.map((category) => (
                          <button
                            key={category}
                            onClick={() => updateFilter('category', category)}
                            className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                              filters.category === category
                                ? 'bg-le-accent text-white'
                                : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                          >
                            {category}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Condition */}
                    <div>
                      <label className="block text-white font-medium mb-3">Condition</label>
                      <div className="grid grid-cols-2 gap-2">
                        {conditions.map((condition) => (
                          <button
                            key={condition}
                            onClick={() => toggleArrayFilter('conditions', condition.toLowerCase())}
                            className={`px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                              (filters.conditions || []).includes(condition.toLowerCase())
                                ? 'bg-le-accent text-white'
                                : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                          >
                            {condition}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Price Range */}
                    <div>
                      <label className="block text-white font-medium mb-3">Price Range</label>
                      <div className="space-y-2">
                        {priceBuckets.map((bucket) => (
                          <button
                            key={bucket}
                            onClick={() => updateFilter('priceRange', bucket)}
                            className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                              filters.priceRange === bucket
                                ? 'bg-le-accent text-white'
                                : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                          >
                            {bucket}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Forum Filters */}
                {type === 'forums' && (
                  <>
                    {/* Category */}
                    <div>
                      <label className="block text-white font-medium mb-3">Category</label>
                      <div className="space-y-2">
                        {forumCategories.map((category) => (
                          <button
                            key={category}
                            onClick={() => updateFilter('category', category)}
                            className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                              filters.category === category
                                ? 'bg-le-accent text-white'
                                : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                          >
                            {category}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Sort */}
                    <div>
                      <label className="block text-white font-medium mb-3">Sort By</label>
                      <div className="space-y-2">
                        {['Latest Activity', 'Most Popular', 'Most Replies', 'Newest'].map((sort) => (
                          <button
                            key={sort}
                            onClick={() => updateFilter('sortBy', sort)}
                            className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                              filters.sortBy === sort
                                ? 'bg-le-accent text-white'
                                : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                          >
                            {sort}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Location (for events and marketplace) */}
                {(type === 'events' || type === 'marketplace') && (
                  <div>
                    <label className="block text-white font-medium mb-3">
                      <MapPin size={16} className="inline mr-2" />
                      Distance
                    </label>
                    <div className="space-y-2">
                      {distances.map((distance) => (
                        <button
                          key={distance}
                          onClick={() => updateFilter('distance', distance)}
                          className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                            filters.distance === distance
                              ? 'bg-le-accent text-white'
                              : 'bg-white/10 text-white/70 hover:bg-white/20'
                          }`}
                        >
                          {distance}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default FilterPanel